/**
 * TaskListWidget - Displays a filtered list of tasks with configurable display
 *
 * Uses the centralized query engine for filtering.
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  Clock,
  Pause,
  AlertCircle,
  XCircle,
  Tag,
  User,
} from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { TaskListConfigModal } from './TaskListConfigModal';
import { ExpandedTaskListModal } from './ExpandedTaskListModal';
import type { TaskListWidgetConfig, TaskDisplayField } from '../../types/widgets';
import { useTodos } from '../../hooks/useTodos';
import { groupTodos } from '../../utils/taskGrouping';
import { useWindows } from '../../contexts/WindowsContext';
import { TaskStatus } from '../../types';
import type { Todo } from '../../types';
import { executeQuery } from '../../utils/queryEngine';
import { buildQueryFromUI } from '../../utils/queryBuilder';

interface TaskListWidgetProps {
  config: TaskListWidgetConfig;
  widgetId: string;
  windowId: string;
}

// Helper to check if a task is overdue (for display purposes)
function isOverdue(todo: Todo): boolean {
  if (!todo.deadline || todo.status === TaskStatus.COMPLETED || todo.status === TaskStatus.CANCELED) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(todo.deadline) < today;
}

// Status icons
const STATUS_ICONS: Record<string, React.ElementType> = {
  [TaskStatus.NOT_STARTED]: Circle,
  [TaskStatus.IN_PROGRESS]: Clock,
  [TaskStatus.COMPLETED]: CheckCircle2,
  [TaskStatus.WAITING]: Pause,
  [TaskStatus.CANCELED]: XCircle,
};

const STATUS_COLORS: Record<string, string> = {
  [TaskStatus.NOT_STARTED]: 'text-gray-400',
  [TaskStatus.IN_PROGRESS]: 'text-blue-500',
  [TaskStatus.COMPLETED]: 'text-green-500',
  [TaskStatus.WAITING]: 'text-yellow-500',
  [TaskStatus.CANCELED]: 'text-red-400',
};

const HORIZON_COLORS: Record<string, string> = {
  now: 'text-red-600 bg-red-50 dark:bg-red-900/30',
  next: 'text-orange-600 bg-orange-50 dark:bg-orange-900/30',
  soon: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30',
  later: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30',
  someday: 'text-gray-500 bg-gray-50 dark:bg-gray-700',
};

export function TaskListWidget({ config, widgetId, windowId }: TaskListWidgetProps) {
  const { todos, updateTodo, isLoading } = useTodos();
  const { updateWidget } = useWindows();
  const [showConfig, setShowConfig] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);

  // Get display fields, defaulting to basic set
  const displayFields = config.displayFields || ['status', 'date', 'horizon'];

  // Build and execute query
  const filteredTodos = useMemo(() => {
    // Determine which query to use
    let query = null;

    if (config.queryMode === 'advanced' && config.query) {
      // Advanced mode: use the stored query directly
      query = config.query;
    } else {
      // Simple mode: build query from UI options
      query = buildQueryFromUI({
        statusFilter: config.statusFilter,
        horizonFilter: config.horizonFilter,
        tagsFilter: config.tagsFilter,
        dateFilter: config.dateFilter,
        dateFilters: config.dateFilters,
        showCompleted: config.showCompleted,
        onlyWithDeadline: config.onlyWithDeadline,
        onlyOverdue: config.onlyOverdue,
      });
    }

    // Execute the query
    let result = executeQuery(todos, query);

    // Apply sorting (separate from filtering)
    const sortDir = config.sortDirection === 'desc' ? -1 : 1;
    switch (config.sortBy) {
      case 'horizon': {
        const horizonOrder: Record<string, number> = { now: 0, next: 1, soon: 2, later: 3, someday: 4 };
        result.sort(
          (a, b) =>
            sortDir * ((horizonOrder[a.horizon ?? ''] ?? 5) - (horizonOrder[b.horizon ?? ''] ?? 5))
        );
        break;
      }
      case 'date':
        result.sort((a, b) => {
          const dateA = a.deadline || a.created_time;
          const dateB = b.deadline || b.created_time;
          return sortDir * (new Date(dateA).getTime() - new Date(dateB).getTime());
        });
        break;
      case 'deadline':
        result.sort((a, b) => {
          if (!a.deadline && !b.deadline) return 0;
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return sortDir * (new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
        });
        break;
      case 'created':
        result.sort(
          (a, b) =>
            sortDir * (new Date(b.created_time).getTime() - new Date(a.created_time).getTime())
        );
        break;
      case 'title':
        result.sort((a, b) => sortDir * a.title.localeCompare(b.title));
        break;
    }

    // Apply item limit
    if (config.maxItems && config.maxItems > 0) {
      result = result.slice(0, config.maxItems);
    }

    return result;
  }, [todos, config]);

  const toggleStatus = (todo: Todo) => {
    const newStatus =
      todo.status === TaskStatus.COMPLETED ? TaskStatus.NOT_STARTED : TaskStatus.COMPLETED;
    updateTodo({ id: todo.id, data: { status: newStatus } });
  };

  const title = config.title || 'Tasks';
  const showCount = config.showTaskCount !== false;
  const compact = config.compactMode === true;

  // Check if a field should be displayed
  const showField = (field: TaskDisplayField) => displayFields.includes(field);

  // Group tasks
  const grouped = useMemo(
    () => groupTodos(filteredTodos, config.groupBy),
    [filteredTodos, config.groupBy]
  );
  const isGrouped = config.groupBy && config.groupBy !== 'none';

  const renderTask = (todo: Todo) => {
    const StatusIcon = STATUS_ICONS[todo.status] || Circle;
    const statusColor = STATUS_COLORS[todo.status] || 'text-gray-400';
    const overdueFlag = isOverdue(todo);

    const handleTaskClick = (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;
      if (mouseDownPos.current) {
        const dx = Math.abs(e.clientX - mouseDownPos.current.x);
        const dy = Math.abs(e.clientY - mouseDownPos.current.y);
        if (dx > 5 || dy > 5) return;
      }
      window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
    };

    return (
      <li
        key={todo.id}
        onMouseDown={(e) => { mouseDownPos.current = { x: e.clientX, y: e.clientY }; }}
        onClick={handleTaskClick}
        className={`flex items-start gap-2 ${
          compact ? 'p-1.5' : 'p-2'
        } rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer group`}
      >
        {showField('status') && (
          <button
            onClick={() => toggleStatus(todo)}
            className={`mt-0.5 flex-shrink-0 ${statusColor} hover:text-green-600 transition-colors`}
          >
            <StatusIcon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p
              className={`${compact ? 'text-xs' : 'text-sm'} truncate ${
                todo.status === TaskStatus.COMPLETED || todo.status === TaskStatus.CANCELED
                  ? 'text-gray-400 line-through'
                  : 'text-gray-800 dark:text-gray-200'
              }`}
            >
              {todo.title}
            </p>
            {overdueFlag && (
              <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
            )}
          </div>
          {showField('description') && todo.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {todo.description}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {showField('deadline') && todo.deadline && (
              <span
                className={`flex items-center gap-0.5 text-xs ${
                  overdueFlag ? 'text-red-500' : 'text-gray-400'
                }`}
              >
                <AlertCircle className="w-3 h-3" />
                Due: {new Date(todo.deadline).toLocaleDateString()}
              </span>
            )}
            {showField('tags') && todo.tags && todo.tags.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-gray-400">
                <Tag className="w-3 h-3" />
                {todo.tags.slice(0, 2).join(', ')}
                {todo.tags.length > 2 && `+${todo.tags.length - 2}`}
              </span>
            )}
            {showField('assignee') && todo.assigned_to_id && (
              <span className="flex items-center gap-0.5 text-xs text-gray-400">
                <User className="w-3 h-3" />
                Assigned
              </span>
            )}
          </div>
        </div>
        {showField('horizon') && todo.horizon && (
          <span
            className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${HORIZON_COLORS[todo.horizon] || 'text-gray-400 bg-gray-50 dark:bg-gray-700'}`}
            title={`${todo.horizon} horizon`}
          >
            {todo.horizon}
          </span>
        )}
      </li>
    );
  };

  return (
    <>
      <WidgetWrapper
        title={showCount ? `${title} (${filteredTodos.length})` : title}
        widgetId={widgetId}
        windowId={windowId}
        onSettings={() => setShowConfig(true)}
        onHeaderClick={() => setExpanded(true)}
      >
        <div className={compact ? 'p-1' : 'p-2'}>
          {isLoading ? (
            <div className="flex items-center justify-center h-20 text-gray-400">Loading...</div>
          ) : filteredTodos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-20 text-gray-400">
              <p className="text-sm">No tasks match filters</p>
            </div>
          ) : isGrouped ? (
            <div className="space-y-3">
              {grouped.map((group) => (
                <div key={group.key}>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-2 mb-1">
                    {group.label} ({group.todos.length})
                  </h4>
                  <ul className={compact ? 'space-y-0.5' : 'space-y-1'}>
                    {group.todos.map(renderTask)}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className={compact ? 'space-y-0.5' : 'space-y-1'}>
              {filteredTodos.map(renderTask)}
            </ul>
          )}
        </div>
      </WidgetWrapper>

      {/* Config Modal */}
      {showConfig && (
        <TaskListConfigModal
          config={config}
          onSave={(newConfig) => {
            updateWidget(windowId, widgetId, newConfig);
            setShowConfig(false);
          }}
          onClose={() => setShowConfig(false)}
        />
      )}

      {/* Expanded Modal */}
      {expanded && (
        <ExpandedTaskListModal
          config={config}
          isOpen
          onClose={() => setExpanded(false)}
        />
      )}
    </>
  );
}
